import { Scale, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { BalanceSummary } from '../components/settlement/BalanceSummary';
import { UnifiedTransferCard } from '../components/settlement/TransferCard';
import { EmptyState } from '../components/shared/EmptyState';
import { useEvents } from '../context/EventContext';
import { useBalances } from '../hooks/useBalances';
import { useAllTransfers } from '../hooks/useTransfers';
import { formatCurrency } from '../lib/currency';

export function SettlementTab() {
  const { currentEvent } = useEvents();

  const { participantBalances, groupBalances, unassignedBalances, totalSpent } =
    useBalances(currentEvent);

  const allTransfers = useAllTransfers(groupBalances, unassignedBalances);

  if (!currentEvent) return null;

  const { currency, expenses, participants } = currentEvent;

  const hasExpenses = expenses.length > 0;
  const hasTransfers = allTransfers.length > 0;

  if (!hasExpenses) {
    return (
      <div className="p-4 pb-20">
        <Card>
          <CardContent className="py-6">
            <EmptyState
              icon={<Scale className="h-8 w-8" />}
              title="Sin gastos para calcular"
              description="Agrega gastos en la pestaña correspondiente"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (participants.length === 0) {
    return (
      <div className="p-4 pb-20">
        <Card>
          <CardContent className="py-6">
            <EmptyState
              icon={<Scale className="h-8 w-8" />}
              title="Sin participantes"
              description="Agrega participantes para ver el balance"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 pb-20 space-y-6">
      {/* Summary */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Total del evento</span>
            <span className="text-lg font-semibold">
              {formatCurrency(totalSpent, currency)}
            </span>
          </div>
          {participants.length > 0 && (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
              <span className="text-sm text-muted-foreground">Por persona</span>
              <span className="text-sm font-medium">
                {formatCurrency(Math.round(totalSpent / participants.length), currency)}
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transfers needed */}
      <section>
        <h2 className="text-sm font-medium text-muted-foreground mb-3">
          Transferencias necesarias
        </h2>

        {!hasTransfers ? (
          <Card>
            <CardContent className="py-6">
              <EmptyState
                icon={<CheckCircle2 className="h-8 w-8 text-green-500" />}
                title="Todo saldado"
                description="No hay transferencias pendientes"
              />
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {allTransfers.map((transfer, index) => (
              <UnifiedTransferCard
                key={`transfer-${index}`}
                transfer={transfer}
                currency={currency}
              />
            ))}
          </div>
        )}
      </section>

      {/* Balance details */}
      <section>
        <h2 className="text-sm font-medium text-muted-foreground mb-3">
          Detalle de balances
        </h2>
        <BalanceSummary
          participantBalances={participantBalances}
          groupBalances={groupBalances}
          currency={currency}
        />
      </section>
    </div>
  );
}
